import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BaseButton } from '@/components/uiKit/Button';
import { BaseTabs } from '@/components';
import { HealthStatusChangeTable } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/HealthStatusChangeTable';
import { BeltMisalignmentTable } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/BeltMisalignmentTable';
import { RockSizeTable } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/RockSizeTable';
import { useNotificationPreferences } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/useNotificationPreferences';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { NotificationPreferencesFilter } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/NotificationPreferencesFilter';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { getNotificationPreferencesFilteredAssets } from '@/store/slices/notificationPreferencesSlice/utils';
import { useAppFilters } from '@/hooks';
import { ConfirmationResult, confirmDialog } from './confirmDialog';

export type NotificationPreferencesTableRef = {
  triggerSave: (callback?: () => void) => void;
};

export const NotificationPreferences = () => {
  const { tab } = useAppFilters();
  const filters = useAppFilters();

  const { currentFiltersValues } = useSelector((state: RootState) => state.notificationPreferences);
  const {
    notificationPreferencesAssets,
    notificationPreferencesAssetsAllAssets,
    isLoadingNotificationPreferences,
    isUpdating,
    updateNotificationPreferences,
  } = useNotificationPreferences();

  const healthStatusChangeTableRef = useRef<NotificationPreferencesTableRef>(null);
  const beltMisalignmentTableRef = useRef<NotificationPreferencesTableRef>(null);
  const rockSizeTableRef = useRef<NotificationPreferencesTableRef>(null);

  const [isHealthStatusChangeTableDirty, setIsHealthStatusChangeTableDirty] = useState<boolean>(false);
  const [isBeltMisalignmentTableDirty, setIsBeltMisalignmentTableDirty] = useState<boolean>(false);
  const [isRockSizeTableDirty, setIsRockSizeTableDirty] = useState<boolean>(false);

  const tableData = useMemo(() => {
    const filteredData = getNotificationPreferencesFilteredAssets(
      notificationPreferencesAssetsAllAssets,
      currentFiltersValues
    );
    return {
      healthStatusChange: filteredData?.filter((entry: any) => entry.feature_name === 'health_status_change'),
      beltMisalignment: filteredData?.filter((entry: any) => entry.feature_name === 'belt_misalignment'),
      rockSize: filteredData?.filter((entry: any) => entry.feature_name === 'rock_size'),
    };
  }, [notificationPreferencesAssetsAllAssets, currentFiltersValues]);

  const notificationPreferencesTabs = useMemo(() => {
    return [
      {
        id: 'health-status-change',
        title: 'Health Status Change',
        element: (
          <HealthStatusChangeTable
            data={tableData.healthStatusChange}
            ref={healthStatusChangeTableRef}
            setIsDirty={setIsHealthStatusChangeTableDirty}
            updateNotificationPreferences={updateNotificationPreferences}
          />
        ),
      },
      {
        id: 'belt-misalignment',
        title: 'Belt Misalignment',
        element: (
          <BeltMisalignmentTable
            data={tableData.beltMisalignment}
            ref={beltMisalignmentTableRef}
            setIsDirty={setIsBeltMisalignmentTableDirty}
            updateNotificationPreferences={updateNotificationPreferences}
          />
        ),
      },
      {
        id: 'rock-size',
        title: 'Rock Size',
        element: (
          <RockSizeTable
            data={tableData.rockSize}
            ref={rockSizeTableRef}
            setIsDirty={setIsRockSizeTableDirty}
            updateNotificationPreferences={updateNotificationPreferences}
          />
        ),
      },
    ];
  }, [tableData, currentFiltersValues, notificationPreferencesAssets]);

  const handleSaveChanges = useCallback(() => {
    [healthStatusChangeTableRef, beltMisalignmentTableRef, rockSizeTableRef].forEach((ref) => {
      ref.current?.triggerSave();
    });
  }, []);

  const renderRightPane = useCallback(() => {
    return (
      <div className="flex items-center min-w-full justify-between gap-6">
        <div className="flex items-center justify-start pl-6 gap-6">
          <Separator className="mx-4 h-6 xl:h-10 bg-gray-300" orientation="vertical" />
          <NotificationPreferencesFilter notificationPreferencesAssets={notificationPreferencesAssets} />
        </div>

        <div className="flex items-center justify-end pl-6 gap-6">
          <BaseButton variant="filled" size="xl" onClick={handleSaveChanges}>
            {'Save Changes'}
          </BaseButton>
        </div>
      </div>
    );
  }, [notificationPreferencesAssets]);

  const onTabClick = async (tabId: string) => {
    if (isHealthStatusChangeTableDirty || isBeltMisalignmentTableDirty || isRockSizeTableDirty) {
      const confirmDialogResult = await confirmDialog();

      switch (confirmDialogResult) {
        case ConfirmationResult.SAVE_AND_EXIT:
          handleSaveChanges();
          filters.setFilters({ ['tab']: tabId });
          break;
        case ConfirmationResult.DONT_SAVE:
          setIsHealthStatusChangeTableDirty(false);
          setIsBeltMisalignmentTableDirty(false);
          setIsRockSizeTableDirty(false);

          filters.setFilters({ ['tab']: tabId });
          break;
        case ConfirmationResult.CANCEL:
          filters.setFilters({ ['tab']: tab });
          break;
      }
    } else {
      filters.setFilters({ ['tab']: tabId });
    }
  };

  if (isLoadingNotificationPreferences) {
    return <Skeleton className="h-full" />;
  }

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex flex-row w-full justify-between gap-4 items-start">
        <div className="flex flex-row w-full">
          <BaseTabs
            tabs={notificationPreferencesTabs}
            defaultValue={notificationPreferencesTabs[0].id}
            hideTabTriggers={false}
            renderRightPane={renderRightPane}
            onClick={onTabClick}
            confirmBeforeChange
          />
        </div>
      </div>
    </div>
  );
};
